import { FormField } from '@/components/forms/form-field'
import { PasswordInput } from '@/components/forms/password-input'
import { AuthLayout } from '@/components/layout/auth-layout'
import { Button } from '@/components/primitives/button'
import { LanguageSegmentedControl } from '@/features/auth/components/language-segmented-control'
import { MobilePasswordResetPage } from '@/features/auth/mobile/mobile-password-reset-page'
import { usePasswordResetPage } from '@/features/auth/password-reset/use-password-reset-page'
import { setAppLanguage } from '@/i18n/i18n'
import type { PortalKind } from '@/lib/auth/token-storage'
import { useMediaQuery } from '@/lib/use-media-query'

interface PasswordResetPageProps {
  portal: PortalKind
}

export function PasswordResetPage({ portal }: PasswordResetPageProps) {
  const isMobile = useMediaQuery('(max-width: 767px)')
  if (portal === 'user' && isMobile) return <MobilePasswordResetPage />
  return <DesktopPasswordResetPage portal={portal} />
}

function DesktopPasswordResetPage({ portal }: PasswordResetPageProps) {
  const {
    cancel,
    confirmPassword,
    currentPassword,
    errors,
    language,
    newPassword,
    resetMutation,
    setConfirmPassword,
    setCurrentPassword,
    setErrors,
    setNewPassword,
    t,
    validate,
  } = usePasswordResetPage(portal)

  return (
    <AuthLayout
      actions={
        <LanguageSegmentedControl
          language={language}
          onLanguageChange={(nextLanguage) => {
            void setAppLanguage(nextLanguage)
          }}
          t={t}
        />
      }
      cardClassName="min-h-0 max-w-[400px] translate-y-0 px-8 py-8 sm:px-10"
      portal={portal}
    >
      <div className="grid gap-6">
        <div className="grid gap-2 text-center">
          <h2 className="text-[22px] font-semibold leading-8 text-[var(--color-text)]">
            {t('passwordReset.title')}
          </h2>
          <p className="text-sm leading-6 text-[var(--color-text-muted)]">
            {t('passwordReset.subtitle')}
          </p>
        </div>

        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (!validate()) {
              return
            }
            resetMutation.mutate()
          }}
        >
          <FormField
            error={errors.currentPassword}
            htmlFor={`${portal}-reset-current-password`}
            label={t('passwordReset.currentPassword')}
            required
          >
            <PasswordInput
              autoComplete="current-password"
              hideLabel={t('common.hidePassword')}
              id={`${portal}-reset-current-password`}
              invalid={Boolean(errors.currentPassword)}
              onChange={(event) => {
                setCurrentPassword(event.target.value)
                setErrors((current) => ({
                  ...current,
                  currentPassword: undefined,
                  root: undefined,
                }))
              }}
              placeholder={t('passwordReset.currentPasswordPlaceholder')}
              showLabel={t('common.showPassword')}
              value={currentPassword}
            />
          </FormField>

          <FormField
            error={errors.newPassword}
            htmlFor={`${portal}-reset-new-password`}
            label={t('passwordReset.newPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              hideLabel={t('common.hidePassword')}
              id={`${portal}-reset-new-password`}
              invalid={Boolean(errors.newPassword)}
              onChange={(event) => {
                setNewPassword(event.target.value)
                setErrors((current) => ({ ...current, newPassword: undefined, root: undefined }))
              }}
              placeholder={t('passwordReset.newPasswordPlaceholder')}
              showLabel={t('common.showPassword')}
              value={newPassword}
            />
          </FormField>

          <FormField
            error={errors.confirmPassword}
            htmlFor={`${portal}-reset-confirm-password`}
            label={t('passwordReset.confirmPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              hideLabel={t('common.hidePassword')}
              id={`${portal}-reset-confirm-password`}
              invalid={Boolean(errors.confirmPassword)}
              onChange={(event) => {
                setConfirmPassword(event.target.value)
                setErrors((current) => ({
                  ...current,
                  confirmPassword: undefined,
                  root: undefined,
                }))
              }}
              placeholder={t('passwordReset.confirmPasswordPlaceholder')}
              showLabel={t('common.showPassword')}
              value={confirmPassword}
            />
          </FormField>

          {errors.root ? (
            <p
              className="rounded-md bg-red-50 px-3 py-2 text-xs leading-5 text-[var(--color-danger)]"
              role="alert"
            >
              {errors.root}
            </p>
          ) : null}

          <div className="mt-1 grid gap-3">
            <Button
              className="h-[42px] rounded-lg text-base font-bold"
              isLoading={resetMutation.isPending}
              type="submit"
            >
              {t('passwordReset.submit')}
            </Button>
            <Button
              disabled={resetMutation.isPending}
              onClick={cancel}
              type="button"
              variant="ghost"
            >
              {t('passwordReset.backToLogin')}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  )
}
